import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft, Brain, CheckCircle2, XCircle, ChevronRight, ChevronLeft,
  Sparkles, TrendingUp, BookOpen, Zap, Trophy, Loader2,
} from "lucide-react";

/**
 * StudyTopicPage - the Mastery Loop runner.
 *
 * Stages:
 *   "loading"     → fetching session
 *   "intro"       → "Ready to start? Quick 3-Q diagnostic first."
 *   "pretest"     → 3 questions, no feedback
 *   "pretest_results" → score + which were missed
 *   "learn"       → lesson sections, key takeaways, exam tips
 *   "posttest"    → 6 fresh questions
 *   "complete"    → improvement chart, mastery badge, return-tomorrow review CTA
 */
const StudyTopicPage = () => {
  const navigate = useNavigate();
  const { subjectId, topicId } = useParams();
  const [stage, setStage] = useState("loading");
  const [session, setSession] = useState(null);
  const [topic, setTopic] = useState(null);
  const [pretest, setPretest] = useState([]);
  const [pretestResults, setPretestResults] = useState(null);
  const [posttest, setPosttest] = useState([]);
  const [completeResult, setCompleteResult] = useState(null);
  const [answers, setAnswers] = useState({});
  const [qIdx, setQIdx] = useState(0);
  const [sectionIdx, setSectionIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const subjectLabel = useMemo(() => (subjectId || "").replace(/_/g, " "), [subjectId]);

  // ---------- Start or resume a session ----------
  const storageKey = `study_session:${subjectId}/${topicId}`;

  const startSession = useCallback(async () => {
    setStage("loading");
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          const s = await api.get(`/study/session/${encodeURIComponent(saved)}`);
          if (s.stage === "pretest" || s.stage === "learn" || s.stage === "posttest") {
            setSession(s.session_id);
            setTopic(s.topic);
            setAnswers({});
            setQIdx(0);
            if (s.stage === "pretest") { setPretest(s.pretest); setStage("intro"); return; }
            if (s.stage === "learn") { setSectionIdx(0); setStage("learn"); return; }
            setPosttest(s.posttest); setStage("posttest"); return;
          }
        } catch (_) { /* stale session — fall through and start fresh */ }
        localStorage.removeItem(storageKey);
      }
      const data = await api.post("/study/start", { subject_id: subjectId, topic_id: topicId });
      localStorage.setItem(storageKey, data.session_id);
      setSession(data.session_id);
      setTopic(data.topic);
      setPretest(data.pretest);
      setAnswers({});
      setQIdx(0);
      setStage("intro");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start this topic");
      navigate(`/learn/${subjectId}/${topicId}`);
    }
  }, [subjectId, topicId, navigate, storageKey]);

  useEffect(() => { startSession(); }, [startSession]);

  // ---------- Pretest submit ----------
  const submitPretest = async () => {
    setSubmitting(true);
    try {
      const payload = {
        session_id: session,
        answers: pretest.map((q) => ({
          question_id: q.id,
          selected_option_id: answers[q.id] || q.options[0].id,
          time_taken_seconds: 0,
        })),
      };
      const res = await api.post("/study/pretest/submit", payload);
      setPretestResults(res);
      setStage("pretest_results");
    } catch (e) {
      toast.error("Submit failed - try again");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Start posttest ----------
  const startPosttest = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(
        `/study/posttest/start?session_id=${encodeURIComponent(session)}`,
        { subject_id: subjectId, topic_id: topicId },
      );
      setPosttest(res.posttest);
      setAnswers({});
      setQIdx(0);
      setStage("posttest");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load post-test");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Posttest submit ----------
  const submitPosttest = async () => {
    setSubmitting(true);
    try {
      const payload = {
        session_id: session,
        answers: posttest.map((q) => ({
          question_id: q.id,
          selected_option_id: answers[q.id] || q.options[0].id,
          time_taken_seconds: 0,
        })),
      };
      const res = await api.post("/study/posttest/submit", payload);
      localStorage.removeItem(storageKey);
      setCompleteResult(res);
      setStage("complete");
    } catch (e) {
      toast.error("Submit failed - try again");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Renders ----------

  if (stage === "loading" || !topic) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const stepLabel = (() => {
    switch (stage) {
      case "intro": return "Step 1 of 4 · Pre-check";
      case "pretest": return "Step 1 of 4 · Pre-check";
      case "pretest_results": return "Step 1 of 4 · Pre-check results";
      case "learn": return "Step 2 of 4 · Learn the concept";
      case "posttest": return "Step 3 of 4 · Test yourself";
      case "complete": return "Step 4 of 4 · You're done";
      default: return "";
    }
  })();

  return (
    <div className="max-w-3xl mx-auto py-8 px-4" data-testid="study-topic-page">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/learn/${subjectId}/${topicId}`)}
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
          data-testid="study-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to topic
        </button>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-primary font-semibold">{stepLabel}</div>
          <div className="text-xs text-muted-foreground">{topic.estimated_time || 15} min</div>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold mb-1">{topic.name}</h1>
        <div className="text-sm text-muted-foreground">Mastery loop - diagnostic, lesson, post-test, review.</div>
      </div>

      {stage === "intro" && (
        <IntroStage topic={topic} onStart={() => setStage("pretest")} />
      )}

      {stage === "pretest" && (
        <TestStage
          questions={pretest}
          qIdx={qIdx}
          setQIdx={setQIdx}
          answers={answers}
          setAnswers={setAnswers}
          onSubmit={submitPretest}
          submitting={submitting}
          stageLabel="pretest"
          subjectName={subjectLabel}
        />
      )}

      {stage === "pretest_results" && (
        <PretestResultsStage
          results={pretestResults}
          onContinue={() => { setStage("learn"); setSectionIdx(0); }}
        />
      )}

      {stage === "learn" && (
        <LearnStage
          topic={topic}
          sectionIdx={sectionIdx}
          setSectionIdx={setSectionIdx}
          onComplete={startPosttest}
          submitting={submitting}
        />
      )}

      {stage === "posttest" && (
        <TestStage
          questions={posttest}
          qIdx={qIdx}
          setQIdx={setQIdx}
          answers={answers}
          setAnswers={setAnswers}
          onSubmit={submitPosttest}
          submitting={submitting}
          stageLabel="posttest"
          subjectName={subjectLabel}
        />
      )}

      {stage === "complete" && (
        <CompleteStage
          result={completeResult}
          topic={topic}
          onReturnToTopic={() => navigate(`/learn/${subjectId}/${topicId}`)}
          onStudyAnother={() => navigate(`/learn`)}
        />
      )}
    </div>
  );
};

// =================== STAGES ===================

const IntroStage = ({ topic, onStart }) => (
  <div className="glass rounded-2xl p-6 md:p-8 border border-border space-y-5" data-testid="study-intro">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Sparkles className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h2 className="text-lg font-semibold mb-1">Quick pre-check first</h2>
        <p className="text-sm text-muted-foreground">
          We start with 3 quick questions to see what you already know. Don&apos;t worry about score - this just primes your brain and lets us focus the lesson.
        </p>
      </div>
    </div>
    <ul className="text-sm text-muted-foreground space-y-2 pl-4">
      <li>· <strong className="text-foreground">Pre-check</strong> - 3 questions, no penalty.</li>
      <li>· <strong className="text-foreground">Lesson</strong> - read the concept (~{topic.estimated_time || 15} min).</li>
      <li>· <strong className="text-foreground">Post-test</strong> - 6 questions to confirm mastery.</li>
      <li>· <strong className="text-foreground">Review</strong> - anything you miss comes back tomorrow.</li>
    </ul>
    <Button onClick={onStart} className="w-full rounded-full py-6 text-base" data-testid="study-start-pretest">
      Begin pre-check <ChevronRight className="w-4 h-4 ml-1" />
    </Button>
  </div>
);

const TestStage = ({ questions, qIdx, setQIdx, answers, setAnswers, onSubmit, submitting, stageLabel, subjectName }) => {
  const q = questions[qIdx];
  if (!q) return null;
  const selected = answers[q.id];
  const allAnswered = questions.every((qq) => !!answers[qq.id]);
  return (
    <div className="space-y-5" data-testid={`study-${stageLabel}`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Question {qIdx + 1} of {questions.length}</span>
        <div className="flex-1 h-1 bg-secondary rounded-full overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${((qIdx + 1) / questions.length) * 100}%` }} />
        </div>
      </div>
      <div className="glass rounded-2xl p-5 md:p-7 border border-border">
        {q.topic_match === false && (
          <div className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground bg-secondary/60 rounded-full px-2.5 py-1 mb-3" data-testid="study-related-badge">
            Related · wider {subjectName || "subject"} question
          </div>
        )}
        {q.clinical_vignette && (
          <div className="text-sm bg-secondary/40 rounded-lg p-3 mb-4 italic text-muted-foreground">
            {q.clinical_vignette}
          </div>
        )}
        <div className="font-semibold text-base md:text-lg mb-5">{q.question_text}</div>
        <div className="space-y-2">
          {q.options.map((opt) => (
            <button
              key={opt.id}
              data-testid={`study-option-${opt.id}`}
              onClick={() => setAnswers({ ...answers, [q.id]: opt.id })}
              className={`w-full text-left p-3 rounded-xl border-2 transition-all text-sm ${
                selected === opt.id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/40 hover:bg-secondary/40"
              }`}
            >
              <span className="inline-block w-6 h-6 rounded-md bg-secondary text-center text-xs font-semibold leading-6 mr-3">
                {opt.id.toUpperCase()}
              </span>
              {opt.text}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => setQIdx((i) => Math.max(0, i - 1))}
          disabled={qIdx === 0}
          className="rounded-full"
          data-testid="study-prev"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        {qIdx < questions.length - 1 ? (
          <Button
            onClick={() => setQIdx((i) => i + 1)}
            disabled={!selected}
            className="flex-1 rounded-full"
            data-testid="study-next"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={onSubmit}
            disabled={!allAnswered || submitting}
            className="flex-1 rounded-full"
            data-testid={`study-submit-${stageLabel}`}
          >
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>) : "Submit"}
          </Button>
        )}
      </div>
    </div>
  );
};

const PretestResultsStage = ({ results, onContinue }) => {
  const pct = results.pretest_score ?? 0;
  return (
    <div className="space-y-5" data-testid="study-pretest-results">
      <div className="glass rounded-2xl p-6 border border-border text-center">
        <div className="text-5xl font-bold mb-1">{pct}%</div>
        <div className="text-sm text-muted-foreground mb-4">
          {results.pretest_correct} of {results.pretest_total} correct on the pre-check.
        </div>
        <div className="text-sm">
          {pct >= 67
            ? "Solid baseline - the lesson will sharpen and fill any gaps."
            : "Plenty of room to grow - the lesson below will get you there."}
        </div>
      </div>
      <Button onClick={onContinue} className="w-full rounded-full py-6 text-base" data-testid="study-go-learn">
        Start the lesson <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
};

const LearnStage = ({ topic, sectionIdx, setSectionIdx, onComplete, submitting }) => {
  const sections = topic.sections || [];
  const lastSection = sectionIdx === sections.length - 1;
  const hasSections = sections.length > 0;
  if (!hasSections) {
    return (
      <div className="space-y-5" data-testid="study-learn">
        <div className="glass rounded-2xl p-6 border border-border">
          <h3 className="text-lg font-semibold mb-2">Key takeaways</h3>
          <ul className="space-y-2 text-sm">
            {(topic.key_takeaways || []).map((k, i) => (
              <li key={i} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" /><span>{k}</span></li>
            ))}
          </ul>
        </div>
        <Button onClick={onComplete} disabled={submitting} className="w-full rounded-full py-6 text-base" data-testid="study-go-posttest">
          {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</>) : (<>Test myself <ChevronRight className="w-4 h-4 ml-1" /></>)}
        </Button>
      </div>
    );
  }
  const sec = sections[sectionIdx];
  return (
    <div className="space-y-5" data-testid="study-learn">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Section {sectionIdx + 1} of {sections.length}</span>
        <div className="flex-1 h-1 bg-secondary rounded-full overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${((sectionIdx + 1) / sections.length) * 100}%` }} />
        </div>
      </div>
      <div className="glass rounded-2xl p-6 md:p-8 border border-border">
        <h3 className="text-lg md:text-xl font-bold mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          {sec.title}
        </h3>
        <div className="prose prose-sm max-w-none text-sm leading-relaxed">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // eslint-disable-next-line react/no-unstable-nested-components
              strong: ({ children }) => <strong className="text-primary font-semibold">{children}</strong>,
              // eslint-disable-next-line react/no-unstable-nested-components
              ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>,
              // eslint-disable-next-line react/no-unstable-nested-components
              p: ({ children }) => <p className="mb-3">{children}</p>,
              // eslint-disable-next-line react/no-unstable-nested-components
              table: ({ children }) => (
                <div className="overflow-x-auto my-4">
                  <table className="w-full border-collapse text-sm">{children}</table>
                </div>
              ),
              // eslint-disable-next-line react/no-unstable-nested-components
              th: ({ children }) => (
                <th className="border border-border bg-secondary px-3 py-2 text-left font-medium">{children}</th>
              ),
              // eslint-disable-next-line react/no-unstable-nested-components
              td: ({ children }) => <td className="border border-border px-3 py-2">{children}</td>,
            }}
          >
            {sec.content || ""}
          </ReactMarkdown>
        </div>
      </div>
      {lastSection && (topic.exam_tips || []).length > 0 && (
        <div className="rounded-2xl p-5 border border-primary/30 bg-primary/5">
          <div className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
            <Zap className="w-4 h-4" /> Exam tips
          </div>
          <ul className="space-y-1.5 text-sm">
            {topic.exam_tips.map((tip, i) => <li key={i}>· {tip}</li>)}
          </ul>
        </div>
      )}
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => setSectionIdx((i) => Math.max(0, i - 1))}
          disabled={sectionIdx === 0}
          className="rounded-full"
          data-testid="study-section-prev"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        {!lastSection ? (
          <Button onClick={() => setSectionIdx((i) => i + 1)} className="flex-1 rounded-full" data-testid="study-section-next">
            Next section <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={onComplete} disabled={submitting} className="flex-1 rounded-full" data-testid="study-go-posttest">
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</>) : (<>Test myself <ChevronRight className="w-4 h-4 ml-1" /></>)}
          </Button>
        )}
      </div>
    </div>
  );
};

const CompleteStage = ({ result, topic, onReturnToTopic, onStudyAnother }) => {
  const improvement = result.improvement || 0;
  const post = result.posttest_score || 0;
  return (
    <div className="space-y-5" data-testid="study-complete">
      <div className="glass rounded-2xl p-6 md:p-8 border border-border text-center space-y-3">
        <div className="inline-flex w-14 h-14 rounded-full bg-primary/10 items-center justify-center">
          {result.mastered ? <Trophy className="w-7 h-7 text-primary" /> : <Brain className="w-7 h-7 text-primary" />}
        </div>
        <h2 className="text-xl font-bold">{result.mastered ? "Topic mastered" : "Topic complete"}</h2>
        <div className="flex justify-center gap-6 text-sm py-3">
          <div>
            <div className="text-2xl font-bold">{result.pretest_score || 0}%</div>
            <div className="text-xs text-muted-foreground">Pre-check</div>
          </div>
          <div className="self-center">
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </div>
          <div>
            <div className={`text-2xl font-bold ${post >= 80 ? "text-primary" : ""}`}>{post}%</div>
            <div className="text-xs text-muted-foreground">Post-test</div>
          </div>
          <div className="self-center">
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
              improvement > 0 ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"
            }`}>
              <TrendingUp className="w-3 h-3" />
              {improvement > 0 ? `+${improvement}` : improvement}%
            </div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          You earned <strong>+{result.xp_earned} XP</strong>.
          {result.wrong_question_ids?.length > 0 && (
            <> The {result.wrong_question_ids.length} question(s) you missed will return in your <strong>daily review tomorrow</strong>.</>
          )}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Button variant="outline" onClick={onReturnToTopic} className="rounded-full" data-testid="study-back-to-topic">
          Back to topic
        </Button>
        <Button onClick={onStudyAnother} className="rounded-full" data-testid="study-next-topic">
          Study another topic
        </Button>
      </div>
    </div>
  );
};

export default StudyTopicPage;
